/**
 * Helpers for reading a value out of an add-on tier list.
 *
 * Tier lists are not dense arrays indexed 0..n. `createTiersFromUnitPrice` builds a sparse window
 * around the current selection and leaves holes, and the `subscribed*TierIndex` getters return the
 * subscribed *quantity* to be used as an index. A quantity the customer holds but cannot
 * re-purchase - a migrated KTV allowance with no matching price tier, or any add-on whose pricing
 * KSM could not resolve - is therefore a legitimately absent index even with pricing fully loaded.
 *
 * Every lookup has to tolerate that hole. Reading a field off the hole throws, and these lists are
 * read from computeds that several pages render, so one missing tier blanks whichever page is
 * reading it.
 */

/**
 * Reads a numeric field off a tier, falling back to 0 when the tier or the field is absent.
 * @param {Array|undefined} tiers
 * @param {number} index
 * @param {string} field
 * @returns {number}
 */
export const tierAmount = (tiers, index, field) => Number(tiers?.[index]?.[field]) || 0;

/**
 * Reads a tier's price, falling back to 0 when the tier is absent.
 * @param {Array|undefined} tiers
 * @param {number} index
 * @returns {number}
 */
export const tierPrice = (tiers, index) => tierAmount(tiers, index, 'price');

/**
 * Reads a usable unit price out of a pricing payload, or null when there isn't one.
 *
 * `Number(null)` and `Number('')` are both 0, so coercing first turns a missing price into a free
 * ladder - the exact outcome the callers return an empty list to avoid.
 *
 * Zero is rejected for the same reason, and that is a catalog fact rather than a guess. Checked
 * 2026-10-19 against /api/plans/list/allPlans in dev, staging and prod: the only editable add-ons
 * published at USD 0.00 are addon-5-keap-addcontacts-annual and addon-5-keap-textvoice-annual, and
 * Recurly - which is what actually bills - holds real tiered prices for both (contacts 346..5760,
 * text & voice 168..3348). So a 0.00 here means "OOE did not publish a price", never "this add-on
 * is free". Neither of those two is priced through this helper today (it serves additional users
 * and message bundles, which are priced everywhere), so this is defence, not current behaviour.
 * If a genuinely free add-on ever ships, this is the line to revisit.
 *
 * @param {unknown} raw
 * @returns {number|null}
 */
export const parseUnitPrice = (raw) => {
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }

    const unitPrice = Number(raw);

    return Number.isFinite(unitPrice) && unitPrice > 0 ? unitPrice : null;
};
